// src/hr/timeoff/BalanceHistory.js -- the employee's own balance ledger (GET /api/hr/me/ledger),
// newest first: date, type, what happened in plain words, plus or minus days, and the reason.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiClock } from 'react-icons/fi';
import { hr } from '../api';
import { Btn, Drawer, EmptyState, Skeleton } from '../kit';
import { timestampLabel } from './dates';
import { LEDGER_KINDS, signedDays } from './labels';

function useLedger() {
  const [state, setState] = useState({ status: 'loading', rows: [] });
  const live = useRef(true);
  const load = useCallback(async () => {
    setState({ status: 'loading', rows: [] });
    try {
      const body = await hr.myLedger();
      if (live.current) setState({ status: 'ready', rows: (body && body.rows) || [] });
    } catch (err) {
      if (live.current) setState({ status: 'error', rows: [] });
    }
  }, []);
  useEffect(() => {
    live.current = true;
    load();
    return () => { live.current = false; };
  }, [load]);
  return { ...state, reload: load };
}

function History({ me, onClose }) {
  const { status, rows, reload } = useLedger();
  const sickNames = new Set(((me && me.types) || []).filter((t) => t.isSick).map((t) => t.name));
  const typeName = (name) => (sickNames.has(name) ? 'Sick & Safe' : name);

  return (
    <Drawer
      open
      onClose={onClose}
      title="Balance history"
      subtitle="Every change to your balances, newest first."
      width={440}
    >
      {status === 'loading' && <Skeleton label="Loading your balance history" lines={6} height={12} />}
      {status === 'error' && (
        <div className="hrp-form-err" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>Couldn't load your balance history.</span>
          <Btn variant="ghost" size="sm" onClick={reload}>Try again</Btn>
        </div>
      )}
      {status === 'ready' && rows.length === 0 && (
        <EmptyState compact icon={FiClock} title="No balance changes yet">
          Allowances, carry-overs and time you take will be listed here.
        </EmptyState>
      )}
      {status === 'ready' && rows.length > 0 && (
        <ul className="hrp-led">
          {rows.map((row, i) => {
            const hours = Number(row.hours) || 0;
            return (
              // Ledger rows carry no id of their own; the list never reorders while open.
              <li key={`${row.at}-${i}`} className="hrp-led__row">
                <div className="hrp-led__main">
                  <b>{LEDGER_KINDS[row.kind] || 'Balance change'}</b>
                  <span className="hrp-led__meta">
                    {[typeName(row.typeName), timestampLabel(row.at)].filter(Boolean).join(' · ')}
                  </span>
                  {row.reason && <span className="hrp-led__why">{row.reason}</span>}
                </div>
                <span className="hrp-led__amt" data-sign={hours > 0 ? 'up' : hours < 0 ? 'down' : undefined}>
                  {signedDays(hours)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}

export function BalanceHistory({ open, ...props }) {
  if (!open) return null;
  return <History {...props} />;
}

export default BalanceHistory;
